// =============================================================================
// CustomerAppointmentsModal - Storico e prossimi appuntamenti di un cliente
// =============================================================================

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { useTenantTimeZone } from '../../store/tenantStore';
import { formatInTz, timeStringInTz } from '../../lib/tenantTime';
import {
  X,
  CalendarClock,
  History,
  Loader2,
  AlertCircle,
  Users,
  MapPin,
  Mail,
  Phone,
} from 'lucide-react';
import { useCustomerAppointments } from '../../api/hooks';
import { useDateLocale } from '../../hooks/useDateLocale';
import { cn } from '../../lib/utils';
import type { AppointmentStatus, Customer, CustomerAppointment } from '../../types';

// ===========================================================================
// Status Badge
// ===========================================================================

const STATUS_STYLES: Record<AppointmentStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-orange-100 text-orange-700',
  RESCHEDULED: 'bg-purple-100 text-purple-700',
};

function StatusBadge({ status }: { status: AppointmentStatus }) {
  const { t } = useTranslation('common');

  return (
    <span
      className={cn(
        'px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
        STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
      )}
    >
      {t(`appointmentStatus.${status}`)}
    </span>
  );
}

// ===========================================================================
// Appointment Row
// ===========================================================================

interface AppointmentRowProps {
  appointment: CustomerAppointment;
  onSelect?: (appointment: CustomerAppointment) => void;
}

function AppointmentRow({ appointment, onSelect }: AppointmentRowProps) {
  const { t } = useTranslation('customers');
  const locale = useDateLocale();
  const timeZone = useTenantTimeZone();

  const start = parseISO(appointment.scheduledStart);
  const end = parseISO(appointment.scheduledEnd);
  const isInactive = appointment.status === 'CANCELLED' || appointment.status === 'NO_SHOW';

  return (
    <div
      {...(onSelect
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onSelect(appointment),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(appointment);
              }
            },
          }
        : {})}
      className={cn(
        'w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
        onSelect && 'hover:bg-gray-50 cursor-pointer'
      )}
    >
      {/* Barra colore servizio */}
      <span
        className="w-1 self-stretch rounded-full shrink-0"
        style={{ backgroundColor: appointment.color }}
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('font-medium text-gray-900 truncate', isInactive && 'text-gray-500 line-through')}>
            {appointment.title}
          </span>
          <StatusBadge status={appointment.status} />
          {appointment.isGroupEvent && (
            <span
              className="inline-flex items-center gap-1 text-xs text-gray-500"
              title={t('history.groupEvent')}
            >
              <Users className="w-3 h-3" />
              {appointment.attendanceRole === 'participant' && t('history.asParticipant')}
            </span>
          )}
        </div>

        <div className="mt-0.5 text-sm text-gray-500">
          {formatInTz(start, timeZone, 'EEE d MMM yyyy', locale)} · {timeStringInTz(start, timeZone)}–
          {timeStringInTz(end, timeZone)}
        </div>

        {appointment.resourceNames.length > 0 && (
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-400">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{appointment.resourceNames.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Colonna (prossimi / storico)
// ===========================================================================

interface ColumnProps {
  icon: typeof CalendarClock;
  title: string;
  count: number;
  shownCount: number;
  emptyLabel: string;
  appointments: CustomerAppointment[];
  onSelect?: (appointment: CustomerAppointment) => void;
}

function Column({
  icon: Icon,
  title,
  count,
  shownCount,
  emptyLabel,
  appointments,
  onSelect,
}: ColumnProps) {
  const { t } = useTranslation('customers');

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Icon className="w-4 h-4 text-gray-400" />
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{title}</h4>
        <span className="text-xs text-gray-400">({count})</span>
      </div>

      {appointments.length === 0 ? (
        <p className="px-3 py-4 text-sm text-gray-400 italic">{emptyLabel}</p>
      ) : (
        <div className="space-y-1">
          {appointments.map((appointment) => (
            <AppointmentRow
              key={appointment.id}
              appointment={appointment}
              onSelect={onSelect}
            />
          ))}
          {count > shownCount && (
            <p className="px-3 pt-1 text-xs text-gray-400">
              {t('history.andMore', { count: count - shownCount })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Stat Chip
// ===========================================================================

function StatChip({ label, value, tone }: { label: string; value: number; tone?: 'emerald' | 'amber' }) {
  return (
    <div
      className={cn(
        'px-3 py-1.5 rounded-lg border',
        tone === 'emerald' && 'bg-emerald-50 border-emerald-100',
        tone === 'amber' && 'bg-amber-50 border-amber-100',
        !tone && 'bg-gray-50 border-gray-100'
      )}
    >
      <span
        className={cn(
          'font-semibold',
          tone === 'emerald' && 'text-emerald-700',
          tone === 'amber' && 'text-amber-700',
          !tone && 'text-gray-900'
        )}
      >
        {value}
      </span>
      <span className="ml-1.5 text-xs text-gray-500">{label}</span>
    </div>
  );
}

// ===========================================================================
// Main Component
// ===========================================================================

interface CustomerAppointmentsModalProps {
  customer: Customer | null; // null = chiuso
  onClose: () => void;
  onSelectAppointment?: (appointment: CustomerAppointment) => void;
}

export function CustomerAppointmentsModal({
  customer,
  onClose,
  onSelectAppointment,
}: CustomerAppointmentsModalProps) {
  const { t } = useTranslation('customers');
  const { data, isLoading, error } = useCustomerAppointments(customer?.id ?? null);

  // Chiusura con ESC
  useEffect(() => {
    if (!customer) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [customer, onClose]);

  if (!customer) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {t('history.modalTitle', { name: customer.fullName })}
            </h2>
            <div className="mt-1 flex items-center gap-4 text-sm text-gray-500 flex-wrap">
              {customer.email && (
                <span className="flex items-center gap-1.5 min-w-0">
                  <Mail className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </span>
              )}
              {customer.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 shrink-0" />
                  {customer.phone}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            title={t('common:buttons.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Riepilogo metriche */}
        <div className="flex items-center gap-2 flex-wrap px-6 py-3 bg-gray-50/70 border-b border-gray-100">
          <StatChip label={t('history.statTotal')} value={customer.totalAppointments} />
          <StatChip
            label={t('history.statUpcoming')}
            value={customer.upcomingAppointments}
            tone="emerald"
          />
          <StatChip label={t('history.statPast')} value={customer.pastAppointments} />
          {customer.noShowCount > 0 && (
            <StatChip label={t('noShow')} value={customer.noShowCount} tone="amber" />
          )}
        </div>

        {/* Contenuto */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
            </div>
          ) : error || !data ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
              <AlertCircle className="w-4 h-4 text-red-500" />
              {t('history.loadError')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              <Column
                icon={CalendarClock}
                title={t('history.upcomingTitle')}
                count={data.upcomingTotal}
                shownCount={data.upcoming.length}
                emptyLabel={t('history.noUpcoming')}
                appointments={data.upcoming}
                onSelect={onSelectAppointment}
              />
              <Column
                icon={History}
                title={t('history.pastTitle')}
                count={data.pastTotal}
                shownCount={data.past.length}
                emptyLabel={t('history.noPast')}
                appointments={data.past}
                onSelect={onSelectAppointment}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerAppointmentsModal;
